import React, { useState, useEffect, useMemo } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Box from '@amzn/meridian/box';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import LaborPlanGrid from './LaborPlanGrid';
import LaborSummary from './LaborSummary';
import SupportHeadcount from './SupportHeadcount';
import AvailableLaborChart from './AvailableLaborChart';
import RequiredLaborChart from './RequiredLaborChart';
import VolumeChart from './VolumeChart';
import WIPChart from './WIPChart';
import LaborOpportunities from './LaborOpportunities';
import CycleHeader from './CycleHeader';
import { buildAdherenceActuals } from './flowPlannerAdherenceModel';
import { TEXTS } from '../../utils/constants';
import { getCycleDisplayName } from '../../utils/helpers';
import { WORKING_COLOR, BREAK_PAID, BREAK_UNPAID } from './VolumeDistribution';

const Section = ({ children }) => <div className="fp-results-section">{children}</div>;

// What the timing strip under each volume-source period means.
const STRIP_LEGEND = [
  { color: WORKING_COLOR, label: 'Working' },
  { color: BREAK_PAID, label: 'Paid break' },
  { color: BREAK_UNPAID, label: 'Unpaid break' }
];

const StripLegend = () => (
  <div className="fp-strip-legend">
    {STRIP_LEGEND.map(({ color, label }) => (
      <span key={label} className="fp-strip-legend-item">
        <span className="fp-strip-legend-swatch" style={{ background: color }} />
        {label}
      </span>
    ))}
  </div>
);

const ActualsControlBar = ({
  showActuals,
  onToggle,
  loading,
  refreshing,
  error,
  onDismissError,
  showVolumePctNote,
  showKey,
  legend
}) => (
  <div className="fp-actuals-bar">
    {legend}
    <div style={{ flex: 1 }} />
    {loading && (
      <Text type={TEXTS.T3} color="secondary">
        Loading actuals…
      </Text>
    )}
    {!loading && refreshing && (
      <Row spacing="100" alignmentVertical="center">
        <Loader size="small" />
        <Text type={TEXTS.T3} color="secondary">
          Refreshing actuals…
        </Text>
      </Row>
    )}
    <button
      type="button"
      className="fp-actuals-toggle"
      aria-pressed={showActuals}
      onClick={onToggle}
      disabled={loading}>
      {showActuals ? 'Hide actuals' : 'Show actuals'}
    </button>
    {showKey && (
      <span className="fp-actuals-key">
        <span className="fp-plan-actual">Actual</span>
        <span className="fp-plan-actual-sep" aria-hidden="true">
          /
        </span>
        <span className="fp-actuals-key-planned">Planned values</span>
        {showVolumePctNote && (
          <span className="fp-actuals-key-note">&middot; editable % is the planned volume distribution</span>
        )}
      </span>
    )}
    {error && (
      <div className="fp-actuals-warning" role="status">
        {/* Meridian only renders the close (X) when size !== "small". */}
        <Alert type="warning" size="medium" onClose={onDismissError}>
          {error}
        </Alert>
      </div>
    )}
  </div>
);

const ResultsView = ({
  gridData,
  laborSummary,
  availableCycles,
  optimizerAction = null,
  intervals,
  laborAvailabilityChart,
  requiredHCPerInterval,
  availableHCPerInterval,
  paidHoursPerInterval,
  opportunityHCPerInterval,
  volumeWIPChart,
  laborOpportunities,
  adherenceResponse = null,
  endedByInterval = [],
  showActuals = false,
  adherenceLoading = false,
  adherenceRefreshing = false,
  adherenceError = null,
  onToggleActuals,
  onDismissAdherenceError,
  activeRisks = null,
  supportHeadcount = null,
  gridLayout = 'new',
  distribution = null
}) => {
  const sortedCycles = [...availableCycles].sort();
  const [selectedCycle, setSelectedCycle] = useState(sortedCycles[0] || 'CYCLE_1');
  useEffect(() => {
    setSelectedCycle((prev) => (sortedCycles.length > 0 && !sortedCycles.includes(prev) ? sortedCycles[0] : prev));
  }, [availableCycles]);
  const gridCycleData = gridData?.[selectedCycle] || null;
  const currentTPH = Math.round(gridCycleData?.tph || 0);
  const volumeWIPCycleData = volumeWIPChart?.[selectedCycle] || null;

  // Recomputed per cycle since volume/rate are cycle-scoped.
  const adherenceActuals = useMemo(
    () => buildAdherenceActuals(adherenceResponse, intervals, selectedCycle),
    [adherenceResponse, intervals, selectedCycle]
  );

  // Volume is the only actual the grid renders, so the key appears only when this
  // cycle actually has some - not on a failed load or an empty response.
  const hasRenderedActuals = useMemo(
    () =>
      Object.values(adherenceActuals?.byProcess || {}).some((series) => (series?.volume || []).some((v) => v !== null)),
    [adherenceActuals]
  );
  // Active Risks is owned by FlowPlanner and passed in rendered, so it stays
  // available before a plan has produced grid data.
  const activeRisksSection = activeRisks ? <Section>{activeRisks}</Section> : null;

  if (!gridData) {
    return (
      <Box type="fill" spacingInset="400" style={{ width: '100%' }}>
        <Column spacing="400" alignmentHorizontal="stretch" width="100%">
          {activeRisksSection && <div className="fp-results-panel">{activeRisksSection}</div>}
          <Box type="outline" spacingInset="400" style={{ backgroundColor: 'white', textAlign: 'center' }}>
            <Text type={TEXTS.T2} color="secondary">
              Run a plan to see results
            </Text>
          </Box>
        </Column>
      </Box>
    );
  }
  return (
    <Box type="fill" spacingInset="400" style={{ width: '100%' }}>
      <Column spacing="400" alignmentHorizontal="stretch" width="100%">
        {/* Cycle-scoped: everything here changes with the selected cycle. */}
        <div className="fp-results-panel">
          <Section>
            <CycleHeader
              cycles={sortedCycles}
              selectedCycle={selectedCycle}
              onSelect={setSelectedCycle}
              displayName={getCycleDisplayName}
              tph={currentTPH}
              actions={optimizerAction}
            />
            <ActualsControlBar
              showActuals={showActuals}
              onToggle={onToggleActuals}
              loading={adherenceLoading}
              refreshing={adherenceRefreshing}
              error={adherenceError}
              onDismissError={onDismissAdherenceError}
              showKey={showActuals && hasRenderedActuals}
              showVolumePctNote={gridLayout === 'new' && !!distribution}
              legend={gridLayout === 'new' && !!distribution ? <StripLegend /> : null}
            />
          </Section>
          <Section>
            <LaborPlanGrid
              gridCycleData={gridCycleData}
              intervals={intervals}
              actuals={adherenceActuals}
              showActuals={showActuals}
              endedByInterval={showActuals ? endedByInterval : []}
              gridLayout={gridLayout}
              distribution={distribution}
              selectedCycle={selectedCycle}
            />
          </Section>
          {activeRisksSection}
          {supportHeadcount && (
            <Section>
              {/* Scoped to the selected cycle so its own tab strip cannot disagree with
                  the rest of this panel. */}
              <SupportHeadcount
                availableCycles={[selectedCycle]}
                intervals={intervals}
                supportHeadcounts={supportHeadcount.supportHeadcounts}
                overrides={supportHeadcount.overrides}
                onChange={supportHeadcount.onChange}
                joined
              />
            </Section>
          )}
          <Section>
            <VolumeChart volumeChartData={volumeWIPCycleData} intervals={intervals} />
          </Section>
          <Section>
            <WIPChart wipChartData={volumeWIPCycleData} intervals={intervals} />
          </Section>
        </div>

        <div className="fp-results-panel">
          <Section>
            <LaborSummary laborSummary={laborSummary} intervals={intervals} />
          </Section>
        </div>

        <div className="fp-results-panel">
          <Section>
            <AvailableLaborChart laborAvailabilityChart={laborAvailabilityChart} intervals={intervals} />
          </Section>
          <Section>
            <RequiredLaborChart intervals={intervals} gridCycleData={gridCycleData} />
          </Section>
        </div>

        {Array.isArray(laborOpportunities) && laborOpportunities.length > 0 && (
          <div className="fp-results-panel">
            <Section>
              <LaborOpportunities
                requiredHCPerInterval={requiredHCPerInterval}
                availableHCPerInterval={availableHCPerInterval}
                opportunityHCPerInterval={opportunityHCPerInterval}
                requiredLaborHoursPerInterval={laborSummary?.grandTotals?.laborHours || []}
                paidHoursPerInterval={paidHoursPerInterval}
                laborOpportunities={laborOpportunities}
                intervals={intervals}
              />
            </Section>
          </div>
        )}
      </Column>
    </Box>
  );
};
export default ResultsView;
